<template>
  <div>
    <!-- MULTI SELECT, SORT, FILTERS OR ADD ROUTES -->
    <v-sheet
      id="sort-and-filter-gym-routes"
      class="d-flex mb-2 py-1 px-2 border-bottom"
      style="position: sticky; z-index: 1;"
      :style="$vuetify.breakpoint.mobile ? 'top: 64px; scroll-margin-top: 64px' : 'top: 0px'"
    >
      <!-- SWITCH MULTI SELECTION BTN -->
      <v-btn
        v-if="$auth.loggedIn"
        icon
        class="mr-1"
        :color="multipleSelection ? '#743ad5' : null"
        @click="switchMultiSelection()"
      >
        <v-icon>
          {{ mdiCheckboxMultipleMarkedOutline }}
        </v-icon>
      </v-btn>

      <!-- SORT ROUTE -->
      <gym-space-route-sort
        v-model="sort"
        class="flex-grow-1"
        :gym="gym"
        :gym-space="gymSpace"
      />

      <!-- FILTER BTN -->
      <gym-routes-filter-btn
        :gym="gym"
        :gym-space="gymSpace"
        :filter-callback="pushFilter"
        :active="filters.length > 0"
        class="ml-auto pr-2"
      />

      <!-- ADD ROUTES GLOBAL BTN -->
      <div
        v-if="currentUserIsGymAdmin() && gymAuthCan(gym, 'manage_opening')"
        class="pr-2"
      >
        <v-btn
          text
          icon
          color="primary"
          :to="gymSpace ? `${gymSpace.path}/select-sector` : `${gym.path}/select-space`"
          :title="$t('actions.addLine')"
        >
          <v-icon>
            {{ mdiSourceBranchPlus }}
          </v-icon>
        </v-btn>
      </div>
    </v-sheet>

    <div class="px-2">
      <div
        v-if="haveFilters"
        class="mb-2"
      >
        <strong>
          {{ $t('common.filters') }} :
        </strong>
        <template v-for="filter in filters">
          <v-chip
            :key="`filter-${filter.filterKey}`"
            close
            class="mr-1"
            @click:close="removeFilter(filter)"
          >
            <v-icon
              v-if="filter.icon"
              left
            >
              {{ filter.icon }}
            </v-icon>
            {{ filter.text }}
          </v-chip>
        </template>
      </div>

      <!-- Load routes -->
      <div v-if="loadingRoutes">
        <v-skeleton-loader type="list-item-avatar" />
      </div>

      <!-- Routes list -->
      <div
        v-if="!loadingRoutes"
        id="gym-routes-list"
        style="min-height: 100vh"
      >
        <gym-sector-separator
          v-if="filteredSector && sort.column === 'sector'"
          :gym-sector="{ id: filteredSector.id, name: filteredSector.name }"
          :gym="gym"
          :gym-space="gymSpace"
          :show-plan-options="gymSpace !== null"
        />

        <!-- If I have no routes -->
        <div v-if="!haveFilters && gymSpace && sort.column === 'sector' && gymRoutes.length === 0 && currentUserIsGymAdmin()">
          <gym-sector-separator
            v-for="(allSector, allSectorIndex) in gymSpace.gym_sectors"
            :key="`space-sector-index-${allSectorIndex}`"
            :gym-space="gymSpace"
            :gym="gym"
            :gym-sector="allSector"
          />
        </div>

        <!-- Route list -->
        <div
          v-for="(route, routeIndex) in gymRoutes"
          :key="`gym-route-card-${routeIndex}`"
        >
          <!-- Gym Sector separator -->
          <div
            v-if="!haveFilters && sort.column === 'sector' && (routeIndex === 0 || route.gym_sector_id !== gymRoutes[routeIndex - 1].gym_sector_id)"
          >
            <gym-sector-separator
              v-for="(spaceSector, spaceSectorIndex) in sectorsBetween(routeIndex)"
              :key="`space-sector-index${spaceSectorIndex}`"
              :gym-sector="spaceSector"
              :gym="gym"
              :gym-space="gymSpace || route.gym_space"
              :class="routeIndex !== 0 ? 'mt-5' : ''"
              :show-plan-options="gymSpace !== null"
            />
          </div>

          <!-- Open at Separator -->
          <opened-at-separator
            v-if="sort.column === 'opened_at' && (routeIndex === 0 || openedAtDateDif(route.opened_at) !== openedAtDateDif(gymRoutes[routeIndex - 1].opened_at))"
            :opened-at="route.opened_at"
            :class="routeIndex !== 0 ? 'mt-5' : ''"
          />

          <!-- Paginate route liste -->
          <gym-route-list-item
            v-model="selectedRoutes"
            :highlight-sectors="showPlanOptions"
            :gym-route="route"
            class="mb-1"
            :multiple-selection="multipleSelection"
            :switch-multi-selection="switchMultiSelection"
          />
        </div>

        <!-- Load last sector on space after last route -->
        <div v-if="!haveFilters && noMoreDataToLoad && gymSpace && sort.column === 'sector' && gymRoutes.length > 0 && currentUserIsGymAdmin()">
          <gym-sector-separator
            v-for="(allSector, allSectorIndex) in lastSectors()"
            :key="`space-sector-index-${allSectorIndex}`"
            :gym-space="gymSpace"
            :gym="gym"
            :gym-sector="allSector"
          />
        </div>

        <loading-more
          :get-function="getRoutes"
          :loading-more="loadingMoreData"
          :no-more-data="noMoreDataToLoad"
          skeleton-type="list-item-avatar-two-line,list-item-avatar-two-line,list-item-avatar-two-line"
        />

        <p
          v-if="noMoreDataToLoad"
          class="text-center text--disabled"
        >
          {{ $t('components.gymRoute.noMoreToLoad') }}
        </p>
      </div>

      <!-- MULTI CHECK CONFIRMATION -->
      <v-slide-y-reverse-transition>
        <ascent-gym-multi-check-dialog
          v-if="multipleSelection && selectedRoutes.length > 0"
          :selected-routes="selectedRoutes"
          :off-multi-selection-callback="offMultiSelection"
        />
      </v-slide-y-reverse-transition>

      <!--- MULTI CHECK SUCCESS -->
      <ascent-gym-multi-check-success-modal
        ref="ascentGymMultiCheckSuccessModal"
        :gym="gym"
        :ascents-added-count="ascentsAddedCount"
      />
    </div>
  </div>
</template>

<script>
import {
  mdiSourceBranchPlus,
  mdiCheckboxMultipleMarkedOutline,
  mdiTextureBox,
  mdiFilter
} from '@mdi/js'
import { GymRolesHelpers } from '~/mixins/GymRolesHelpers'
import { LoadingMoreHelpers } from '~/mixins/LoadingMoreHelpers'
import { DateHelpers } from '~/mixins/DateHelpers'
import GymSpaceRouteSort from '@/components/gymRoutes/partial/GymSpaceRouteSort'
import GymRouteApi from '~/services/oblyk-api/GymRouteApi'
import GymSectorApi from '~/services/oblyk-api/GymSectorApi'
import GymRoute from '@/models/GymRoute'
import GymSector from '@/models/GymSector'
import GymRouteListItem from '~/components/gymRoutes/GymRouteListItem'
import LoadingMore from '~/components/layouts/LoadingMore'
import GymSectorSeparator from '~/components/gymRoutes/listByGroup/GymSectorSeparator'
import OpenedAtSeparator from '~/components/gymRoutes/listByGroup/OpenedAtSeparator'
import AscentGymMultiCheckDialog from '~/components/ascentGymRoutes/AscentGymMultiCheckDialog'
import AscentGymMultiCheckSuccessModal from '~/components/ascentGymRoutes/AscentGymMultiCheckSuccessModal'
import GymRoutesFilterBtn from '~/components/gymRoutes/partial/GymRoutesFilterBtn'
import OblykApi from '~/services/oblyk-api/OblykApi'

export default {
  name: 'GymSpaceRouteList',
  components: {
    GymRoutesFilterBtn,
    AscentGymMultiCheckSuccessModal,
    AscentGymMultiCheckDialog,
    OpenedAtSeparator,
    GymSectorSeparator,
    LoadingMore,
    GymRouteListItem,
    GymSpaceRouteSort
  },
  mixins: [GymRolesHelpers, LoadingMoreHelpers, DateHelpers],
  props: {
    gym: {
      type: Object,
      required: true
    },
    gymSpace: {
      type: Object,
      default: null
    },
    activeGymRouteId: {
      type: [String, Number],
      default: null
    },
    showPlanOptions: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      sort: { column: 'opened_at', direction: 'asc', dismounted: false },
      filters: [],
      gymRoutes: [],
      loadingRoutes: true,
      firstLoaded: false,
      selectedRoutes: [],
      multipleSelection: false,
      ascentsAddedCount: 0,

      mdiCheckboxMultipleMarkedOutline,
      mdiSourceBranchPlus,
      mdiFilter
    }
  },

  computed: {
    GymSector () {
      return GymSector
    },
    filteredSector () {
      for (const filter of this.filters) {
        if (filter.type === 'sector') {
          return { id: filter.value, name: filter.text }
        }
      }
      return false
    },

    haveFilters () {
      return this.filters && this.filters.length > 0
    },

    activeGymSectorId () {
      return this.$route.query.sector
    }
  },

  watch: {
    sort () {
      this.reloadRoutes()
    },

    gymSpace () {
      this.getRoutes()
    },

    activeGymSectorId () {
      if (this.activeGymSectorId) {
        this.filterBySector(this.activeGymSectorId)
      }
    }
  },

  mounted () {
    this.$root.$on('dismountGymRoutesInSector', (gymId, spaceId, sectorId) => {
      this.dismountRoutes(gymId, spaceId, sectorId)
    })

    this.$root.$on('reloadSpaceRoutes', () => {
      this.reloadRoutes()
    })

    this.sort.column = localStorage.getItem('gym_route_sort_column') || 'sector'
    this.sort.direction = localStorage.getItem('gym_route_sort_direction') || 'asc'
    if (this.activeGymSectorId) {
      this.filterBySector(this.activeGymSectorId)
    } else {
      this.getRoutes()
    }
  },

  beforeDestroy () {
    this.$root.$off('dismountGymRoutesInSector')
    this.$root.$off('reloadSpaceRoutes')
    this.$localforage.gymRoutes.clear()
  },

  methods: {
    getRoutes () {
      const gymRouteApi = new GymRouteApi(this.$axios, this.$auth)
      let promise
      this.moreIsBeingLoaded()
      const filters = this.filters.map((filter) => { return { type: filter.type, value: filter.value } })

      if (this.gymSpace) {
        promise = gymRouteApi
          .paginatedRoutesInSpace(
            this.gymSpace.gym.id,
            this.gymSpace.id,
            this.sort.column,
            this.sort.direction,
            this.page,
            this.sort.dismounted,
            filters
          )
      } else {
        promise = gymRouteApi
          .paginatedRoutesInGym(
            this.gym.id,
            this.sort.column,
            this.sort.direction,
            this.page,
            this.sort.dismounted,
            filters
          )
      }

      promise
        .then((resp) => {
          for (const route of resp.data) {
            this.$localforage.gymRoutes.setItem(route.id, route)
            this.gymRoutes.push(new GymRoute({ attributes: route }))
          }
          this.successLoadingMore(resp)
        })
        .catch((err) => {
          this.$root.$emit('alertFromApiError', err, 'gymRoute')
          this.failureToLoadingMore()
        })
        .finally(() => {
          this.loadingRoutes = false
          this.firstLoaded = true
          this.finallyMoreIsLoaded()
        })
    },

    reloadRoutes () {
      this.loadingRoutes = true
      this.gymRoutes = []
      this.resetLoadMorePageNumber()
      this.getRoutes(false)
    },

    filterBySector (sectorId, sectorName = null) {
      new OblykApi(this.$axios, this.$auth)
        .get(`/gyms/${this.gym.id}/gym_spaces/${this.gymSpace.id}/gym_sectors/${sectorId}`)
        .then((resp) => {
          this.sort.column = 'sector'
          this.sort.direction = 'asc'
          this.pushFilter({
            type: 'sector',
            value: sectorId,
            text: resp.data.name,
            icon: mdiTextureBox
          })
          setTimeout(() => {
            if (this.$vuetify.breakpoint.mobile) {
              document.querySelector('#sort-and-filter-gym-routes').scrollIntoView({ top: 0, behavior: 'smooth' })
            } else {
              document.querySelector('.gym-space-left-side').scrollTo({ top: 0, behavior: 'smooth' })
            }
          }, 300)
        })
    },

    dismountRoutes (gymId, spaceId, sectorId) {
      this.loadingRoutes = true
      new GymSectorApi(this.$axios, this.$auth)
        .dismountRoutes(
          gymId,
          spaceId,
          sectorId
        )
        .then(() => {
          this.getRoutes()
        })
        .catch((err) => {
          if (err.response) {
            this.$root.$emit('alertFromApiError', err, 'gymRoute')
          }
        })
        .finally(() => {
          this.loadingRoutes = false
        })
    },

    sectorsBetween (routeIndex) {
      if (this.gymSpace === null) {
        return [this.gymRoutes[routeIndex].gym_sector]
      }

      const sectors = []
      const currentSector = { ...this.gymSpace.gym_sectors.find((sector) => { return this.gymRoutes[routeIndex].gym_sector.id === sector.id }) }
      if (routeIndex !== 0) {
        const previousSector = { ...this.gymSpace.gym_sectors.find((sector) => { return this.gymRoutes[routeIndex - 1].gym_sector.id === sector.id }) }
        for (const sector of this.gymSpace.gym_sectors) {
          if (sector.order < currentSector.order && sector.order > previousSector.order) {
            sectors.push(sector)
          }
        }
      } else if (this.gymRoutes.length === 1) {
        for (const sector of this.gymSpace.gym_sectors) {
          if (sector.order < currentSector.order) {
            sectors.push(sector)
          }
        }
      }
      sectors.push(currentSector)
      return sectors
    },

    lastSectors () {
      const sectorId = this.sort.direction === 'asc' ? this.gymRoutes[this.gymRoutes.length - 1].gym_sector.id : this.gymRoutes[0].gym_sector.id
      const lastSector = { ...this.gymSpace.gym_sectors.find((sector) => { return sectorId === sector.id }) }
      const sectors = []
      for (const sector of this.gymSpace.gym_sectors) {
        if (sector.order > lastSector.order) {
          sectors.push(sector)
        }
      }
      return sectors
    },

    openedAtDateDif (date) {
      return this.weekDayAndFromNow(date)
    },

    switchMultiSelection () {
      this.selectedRoutes = []
      this.multipleSelection = !this.multipleSelection
    },

    offMultiSelection (ascentsCount) {
      this.selectedRoutes = []
      this.ascentsAddedCount = ascentsCount
      this.multipleSelection = false
      this.$refs.ascentGymMultiCheckSuccessModal.toggleModal(true)
    },

    pushFilter (filter, reload = true) {
      const filterKey = `${filter.type}.${filter.value}`
      for (const currentFilter of this.filters) {
        // Deduplicate filters
        if (filterKey === currentFilter.filterKey) {
          return false
        }
        // Only one type by filters
        if (filter.type === currentFilter.type) {
          this.removeFilter(currentFilter, false)
        }
      }
      this.filters.push({
        filterKey,
        value: filter.value,
        type: filter.type,
        text: filter.text,
        icon: filter.icon
      })
      if (reload) {
        this.reloadRoutes()
      }
    },

    removeFilter (filter, reload = true) {
      if (filter.type === 'sector' && reload === true) {
        this.$root.$emit('setMapView')
        this.sort.column = localStorage.getItem('gym_route_sort_column') || 'sector'
        this.sort.direction = localStorage.getItem('gym_route_sort_direction') || 'asc'
        const query = { ...this.$route.query } || {}
        delete query.sector
        this.$router.push({ path: this.$route.path, query })
      }
      this.filters = this.filters.filter(currentFilter => currentFilter.filterKey !== filter.filterKey)

      if (reload) {
        this.reloadRoutes()
      }
    }
  }
}
</script>
